import type { NPCModelProps, NPCModelRegistry } from './types';

// ============================================
// CHRISTMAS TOWN CHARACTERS
// ============================================

export function SantaClausModel({ world, hovered }: NPCModelProps) {
  const suitColor = world === 'real' ? '#cc0000' : '#aa2222';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Body - round belly */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <sphereGeometry args={[0.3, 12, 12]} />
        <meshStandardMaterial
          color={suitColor}
          roughness={0.7}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* White belt */}
      <mesh position={[0, 0.3, 0]}>
        <torusGeometry args={[0.28, 0.03, 8, 16]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Belt buckle */}
      <mesh position={[0, 0.3, 0.28]}>
        <boxGeometry args={[0.08, 0.06, 0.02]} />
        <meshStandardMaterial color="#FFD700" metalness={0.7} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshStandardMaterial color="#f5d5c5" roughness={0.7} />
      </mesh>
      {/* White beard */}
      <mesh position={[0, 0.7, 0.1]} castShadow>
        <sphereGeometry args={[0.18, 10, 10, 0, Math.PI * 2, Math.PI / 3, Math.PI]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Mustache */}
      <mesh position={[0, 0.8, 0.12]}>
        <capsuleGeometry args={[0.02, 0.08, 4, 8]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Rosy cheeks */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.82, 0.12]}>
          <sphereGeometry args={[0.025, 6, 6]} />
          <meshStandardMaterial color="#ffaaaa" roughness={0.8} />
        </mesh>
      ))}
      {/* Eyes - jolly */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.88, 0.12]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#4a4a4a" />
        </mesh>
      ))}
      {/* Hat */}
      <mesh position={[0, 1.02, 0]} castShadow>
        <coneGeometry args={[0.12, 0.25, 12]} />
        <meshStandardMaterial color={suitColor} roughness={0.7} />
      </mesh>
      {/* Hat trim */}
      <mesh position={[0, 0.92, 0]}>
        <torusGeometry args={[0.13, 0.025, 8, 16]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Pom pom */}
      <mesh position={[0, 1.15, 0]} castShadow>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Arms */}
      {[-1, 1].map((side, i) => (
        <mesh key={i} position={[side * 0.3, 0.5, 0]} rotation={[0, 0, side * 0.3]} castShadow>
          <capsuleGeometry args={[0.06, 0.15, 6, 8]} />
          <meshStandardMaterial color={suitColor} roughness={0.7} />
        </mesh>
      ))}
      {/* White gloves */}
      {[-0.4, 0.4].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0]} castShadow>
          <sphereGeometry args={[0.05, 8, 8]} />
          <meshStandardMaterial color="#ffffff" roughness={0.8} />
        </mesh>
      ))}
      {/* Legs */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.12, 0]} castShadow>
          <capsuleGeometry args={[0.06, 0.1, 6, 8]} />
          <meshStandardMaterial color={suitColor} roughness={0.7} />
        </mesh>
      ))}
      {/* Black boots */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.03, 0.02]} castShadow>
          <boxGeometry args={[0.08, 0.06, 0.1]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

export function ChristmasElfModel({ world, hovered }: NPCModelProps) {
  const clothColor = world === 'real' ? '#228B22' : '#1a5a1a';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.1, 0.4, 8]} />
        <meshStandardMaterial
          color={clothColor}
          roughness={0.7}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Belt */}
      <mesh position={[0, 0.2, 0]}>
        <torusGeometry args={[0.09, 0.015, 6, 12]} />
        <meshStandardMaterial color="#8B0000" />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color="#f5d5c5" roughness={0.7} />
      </mesh>
      {/* Pointed ears */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.58, 0]} rotation={[0, 0, i === 0 ? -0.3 : 0.3]} castShadow>
          <coneGeometry args={[0.02, 0.06, 4]} />
          <meshStandardMaterial color="#f5d5c5" roughness={0.7} />
        </mesh>
      ))}
      {/* Eyes - cheerful */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.57, 0.08]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#228B22" />
        </mesh>
      ))}
      {/* Smile */}
      <mesh position={[0, 0.52, 0.08]}>
        <torusGeometry args={[0.02, 0.005, 6, 8, Math.PI]} />
        <meshStandardMaterial color="#cc6666" />
      </mesh>
      {/* Pointy hat */}
      <mesh position={[0, 0.72, 0]} rotation={[0.2, 0, 0]} castShadow>
        <coneGeometry args={[0.08, 0.2, 8]} />
        <meshStandardMaterial color={clothColor} roughness={0.7} />
      </mesh>
      {/* Hat trim */}
      <mesh position={[0, 0.63, 0]}>
        <torusGeometry args={[0.085, 0.015, 6, 12]} />
        <meshStandardMaterial color="#ffffff" roughness={0.9} />
      </mesh>
      {/* Bell on hat */}
      <mesh position={[0.05, 0.85, 0.05]} castShadow>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#FFD700" metalness={0.7} />
      </mesh>
      {/* Arms */}
      {[-0.12, 0.12].map((x, i) => (
        <mesh key={i} position={[x, 0.35, 0]} rotation={[0, 0, i === 0 ? 0.5 : -0.5]} castShadow>
          <capsuleGeometry args={[0.025, 0.1, 4, 8]} />
          <meshStandardMaterial color={clothColor} roughness={0.7} />
        </mesh>
      ))}
      {/* Legs */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.08, 0]} castShadow>
          <capsuleGeometry args={[0.03, 0.08, 4, 8]} />
          <meshStandardMaterial color="#ff0000" roughness={0.7} />
        </mesh>
      ))}
      {/* Curly toe shoes */}
      {[-0.04, 0.04].map((x, i) => (
        <group key={i} position={[x, 0.02, 0.04]}>
          <mesh castShadow>
            <boxGeometry args={[0.04, 0.03, 0.06]} />
            <meshStandardMaterial color={clothColor} />
          </mesh>
          <mesh position={[0, 0.01, 0.05]} rotation={[0.5, 0, 0]}>
            <sphereGeometry args={[0.015, 6, 6]} />
            <meshStandardMaterial color="#FFD700" metalness={0.6} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

export function ReindeerModel({ world, hovered }: NPCModelProps) {
  const furColor = world === 'real' ? '#8B4513' : '#5a3a2a';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.35, 0]} rotation={[0, 0, 0]} castShadow>
        <capsuleGeometry args={[0.15, 0.4, 8, 16]} />
        <meshStandardMaterial
          color={furColor}
          roughness={0.9}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.45, 0.3]} castShadow>
        <boxGeometry args={[0.15, 0.12, 0.2]} />
        <meshStandardMaterial color={furColor} roughness={0.9} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.42, 0.45]} castShadow>
        <boxGeometry args={[0.08, 0.08, 0.1]} />
        <meshStandardMaterial color={furColor} roughness={0.9} />
      </mesh>
      {/* Rudolph's red nose! */}
      <mesh position={[0, 0.42, 0.52]}>
        <sphereGeometry args={[0.04, 10, 10]} />
        <meshStandardMaterial
          color="#ff0000"
          emissive="#ff0000"
          emissiveIntensity={0.8}
        />
      </mesh>
      {/* Nose glow */}
      <pointLight position={[0, 0.42, 0.55]} color="#ff0000" intensity={1} distance={1.5} />
      {/* Eyes */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.48, 0.38]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color="#2a2a2a" />
        </mesh>
      ))}
      {/* Antlers */}
      {[-0.08, 0.08].map((x, i) => (
        <group key={i} position={[x, 0.55, 0.25]}>
          {/* Main branch */}
          <mesh rotation={[0, 0, i === 0 ? -0.3 : 0.3]} castShadow>
            <cylinderGeometry args={[0.015, 0.02, 0.2, 6]} />
            <meshStandardMaterial color="#4a3a2a" roughness={0.9} />
          </mesh>
          {/* Side branches */}
          <mesh position={[i === 0 ? -0.05 : 0.05, 0.08, 0]} rotation={[0, 0, i === 0 ? -0.8 : 0.8]}>
            <cylinderGeometry args={[0.01, 0.015, 0.1, 5]} />
            <meshStandardMaterial color="#4a3a2a" roughness={0.9} />
          </mesh>
        </group>
      ))}
      {/* Ears */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.5, 0.2]} rotation={[0.3, i === 0 ? -0.3 : 0.3, 0]} castShadow>
          <coneGeometry args={[0.03, 0.08, 4]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      ))}
      {/* Legs */}
      {[[-0.08, 0.12], [0.08, 0.12], [-0.08, -0.12], [0.08, -0.12]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.12, z]} castShadow>
          <cylinderGeometry args={[0.025, 0.03, 0.24, 6]} />
          <meshStandardMaterial color={furColor} roughness={0.9} />
        </mesh>
      ))}
      {/* Hooves */}
      {[[-0.08, 0.12], [0.08, 0.12], [-0.08, -0.12], [0.08, -0.12]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.02, z]} castShadow>
          <cylinderGeometry args={[0.03, 0.03, 0.04, 6]} />
          <meshStandardMaterial color="#2a2a2a" roughness={0.8} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.4, -0.25]} castShadow>
        <sphereGeometry args={[0.04, 6, 6]} />
        <meshStandardMaterial color="#e8e0d0" roughness={0.9} />
      </mesh>
    </group>
  );
}

export function GingerbreadManModel({ world, hovered }: NPCModelProps) {
  const cookieColor = world === 'real' ? '#cd853f' : '#8B4513';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <capsuleGeometry args={[0.1, 0.15, 8, 12]} />
        <meshStandardMaterial
          color={cookieColor}
          roughness={0.9}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={cookieColor} roughness={0.9} />
      </mesh>
      {/* Frosting buttons */}
      {[0.2, 0.28, 0.36].map((y, i) => (
        <mesh key={i} position={[0, y, 0.08]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      ))}
      {/* Frosting squiggle on body */}
      <mesh position={[0, 0.15, 0.09]}>
        <torusGeometry args={[0.06, 0.01, 6, 12]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Eyes - icing */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.53, 0.1]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      ))}
      {/* Eye pupils */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.53, 0.115]}>
          <sphereGeometry args={[0.008, 6, 6]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
      {/* Smile */}
      <mesh position={[0, 0.46, 0.11]}>
        <torusGeometry args={[0.04, 0.008, 6, 12, Math.PI]} />
        <meshStandardMaterial color="#ff0000" />
      </mesh>
      {/* Arms */}
      {[-0.18, 0.18].map((x, i) => (
        <mesh key={i} position={[x, 0.3, 0]} rotation={[0, 0, i === 0 ? 0.8 : -0.8]} castShadow>
          <capsuleGeometry args={[0.035, 0.1, 4, 8]} />
          <meshStandardMaterial color={cookieColor} roughness={0.9} />
        </mesh>
      ))}
      {/* Legs */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.08, 0]} castShadow>
          <capsuleGeometry args={[0.04, 0.08, 4, 8]} />
          <meshStandardMaterial color={cookieColor} roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

// ============================================
// HALLOWEEN TOWN CHARACTERS
// ============================================

export function JackSkellingtonModel({ world, hovered }: NPCModelProps) {
  const boneColor = world === 'spectral' ? '#e8e8e0' : '#f5f5f0';
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Thin body in pinstripe suit */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.12, 0.7, 8]} />
        <meshStandardMaterial
          color="#1a1a1a"
          roughness={0.7}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Pinstripes */}
      {[-0.04, 0, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.45, 0.09]}>
          <boxGeometry args={[0.005, 0.6, 0.001]} />
          <meshStandardMaterial color="#3a3a3a" />
        </mesh>
      ))}
      {/* Skull head */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshStandardMaterial color={boneColor} roughness={0.8} />
      </mesh>
      {/* Eye sockets - large and round */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.98, 0.12]}>
          <circleGeometry args={[0.04, 12]} />
          <meshStandardMaterial color="#000000" />
        </mesh>
      ))}
      {/* Nose hole */}
      <mesh position={[0, 0.9, 0.14]}>
        <circleGeometry args={[0.015, 6]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      {/* Stitched smile */}
      <mesh position={[0, 0.85, 0.13]}>
        <torusGeometry args={[0.06, 0.008, 6, 12, Math.PI]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Stitch lines across mouth */}
      {[-0.04, -0.02, 0, 0.02, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.84, 0.14]} rotation={[0, 0, Math.PI / 2]}>
          <boxGeometry args={[0.015, 0.006, 0.002]} />
          <meshStandardMaterial color="#2a2a2a" />
        </mesh>
      ))}
      {/* Bat bow tie */}
      <mesh position={[0, 0.78, 0.1]}>
        <boxGeometry args={[0.08, 0.03, 0.01]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      {/* Long thin arms */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.55, 0]} rotation={[0, 0, i === 0 ? 0.3 : -0.3]} castShadow>
          <capsuleGeometry args={[0.02, 0.25, 4, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
        </mesh>
      ))}
      {/* Skeletal hands */}
      {[-0.22, 0.22].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0]} castShadow>
          <sphereGeometry args={[0.03, 8, 8]} />
          <meshStandardMaterial color={boneColor} roughness={0.8} />
        </mesh>
      ))}
      {/* Long thin legs */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.1, 0]} castShadow>
          <capsuleGeometry args={[0.025, 0.15, 4, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function SallyModel({ world, hovered }: NPCModelProps) {
  const skinColor = world === 'spectral' ? '#6a8aaa' : '#8aaaca';
  const dressColor = world === 'spectral' ? '#4a3a6a' : '#5a4a7a';
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Patchwork dress body */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <coneGeometry args={[0.18, 0.6, 8]} />
        <meshStandardMaterial
          color={dressColor}
          roughness={0.8}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Patchwork patches on dress */}
      {[[0.1, 0.35, 0.12, '#8a4a5a'], [-0.08, 0.25, 0.15, '#4a8a5a'], [0.05, 0.5, 0.1, '#8a8a4a']].map(([x, y, z, color], i) => (
        <mesh key={i} position={[x as number, y as number, z as number]}>
          <boxGeometry args={[0.06, 0.08, 0.01]} />
          <meshStandardMaterial color={color as string} />
        </mesh>
      ))}
      {/* Head */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.7} />
      </mesh>
      {/* Long yarn-like hair */}
      <mesh position={[0, 0.75, -0.05]} castShadow>
        <capsuleGeometry args={[0.11, 0.35, 8, 12]} />
        <meshStandardMaterial color="#8B0000" roughness={0.9} />
      </mesh>
      {/* Big expressive eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <group key={i} position={[x, 0.78, 0.1]}>
          <mesh>
            <sphereGeometry args={[0.03, 10, 10]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
          <mesh position={[0, 0, 0.02]}>
            <sphereGeometry args={[0.015, 8, 8]} />
            <meshBasicMaterial color="#000000" />
          </mesh>
        </group>
      ))}
      {/* Stitched mouth */}
      <mesh position={[0, 0.7, 0.11]}>
        <torusGeometry args={[0.03, 0.005, 6, 8, Math.PI]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Stitch marks on face */}
      <mesh position={[0.08, 0.75, 0.1]} rotation={[0, 0, 0.3]}>
        <boxGeometry args={[0.02, 0.005, 0.001]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Thin arms (stitched together) */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.5, 0]} rotation={[0, 0, i === 0 ? 0.4 : -0.4]} castShadow>
          <capsuleGeometry args={[0.025, 0.15, 4, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function GhostDogModel({ world, hovered }: NPCModelProps) {
  const ghostColor = world === 'spectral' ? '#e8e8ff' : '#f0f0ff';
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Ghost sheet body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <coneGeometry args={[0.15, 0.4, 8]} />
        <meshStandardMaterial
          color={ghostColor}
          roughness={0.4}
          transparent
          opacity={0.85}
          emissive={highlightColor || '#aaaaff'}
          emissiveIntensity={hovered ? 0.4 : 0.15}
        />
      </mesh>
      {/* Small dog head bump */}
      <mesh position={[0, 0.5, 0.05]} castShadow>
        <sphereGeometry args={[0.08, 10, 10]} />
        <meshStandardMaterial color={ghostColor} roughness={0.4} transparent opacity={0.85} />
      </mesh>
      {/* Floppy ghost ears */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.52, 0]} rotation={[0, 0, i === 0 ? -0.5 : 0.5]} castShadow>
          <coneGeometry args={[0.03, 0.1, 6]} />
          <meshStandardMaterial color={ghostColor} roughness={0.4} transparent opacity={0.85} />
        </mesh>
      ))}
      {/* Glowing pumpkin nose */}
      <mesh position={[0, 0.48, 0.12]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial
          color="#ff6600"
          emissive="#ff6600"
          emissiveIntensity={1.2}
        />
      </mesh>
      <pointLight position={[0, 0.48, 0.15]} color="#ff6600" intensity={1} distance={1} />
      {/* Dark eye holes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.52, 0.08]}>
          <circleGeometry args={[0.015, 8]} />
          <meshStandardMaterial color="#000000" />
        </mesh>
      ))}
      {/* Wavy tail */}
      <mesh position={[0, 0.15, -0.15]} rotation={[0.5, 0, 0]} castShadow>
        <coneGeometry args={[0.03, 0.15, 6]} />
        <meshStandardMaterial color={ghostColor} roughness={0.4} transparent opacity={0.85} />
      </mesh>
      {/* Floating glow effect */}
      <mesh position={[0, 0.1, 0]}>
        <sphereGeometry args={[0.2, 8, 8]} />
        <meshStandardMaterial color="#aaaaff" transparent opacity={0.1} />
      </mesh>
    </group>
  );
}

export function OogieBoogieModel({ world, hovered }: NPCModelProps) {
  const sackColor = world === 'spectral' ? '#6a8a4a' : '#8aaa6a';
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Large burlap sack body */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.4, 12, 12]} />
        <meshStandardMaterial
          color={sackColor}
          roughness={0.95}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Texture seams on body */}
      {[[0, 0.6, 0.4], [0.3, 0.5, 0.25], [-0.25, 0.7, 0.3]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} rotation={[0, 0, i * 0.5]}>
          <boxGeometry args={[0.15, 0.02, 0.01]} />
          <meshStandardMaterial color="#4a5a3a" roughness={1} />
        </mesh>
      ))}
      {/* Head (same burlap) */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.25, 12, 12]} />
        <meshStandardMaterial color={sackColor} roughness={0.95} />
      </mesh>
      {/* Evil grin */}
      <mesh position={[0, 1.02, 0.22]}>
        <torusGeometry args={[0.12, 0.02, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      {/* Sharp teeth */}
      {[-0.08, -0.04, 0, 0.04, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 1.02, 0.22]} rotation={[Math.PI, 0, 0]}>
          <coneGeometry args={[0.015, 0.04, 4]} />
          <meshStandardMaterial color="#e8e8e0" />
        </mesh>
      ))}
      {/* Hollow eyes */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 1.12, 0.2]}>
          <circleGeometry args={[0.05, 8]} />
          <meshStandardMaterial color="#000000" />
        </mesh>
      ))}
      {/* Arm-like appendages */}
      {[-0.4, 0.4].map((x, i) => (
        <mesh key={i} position={[x, 0.7, 0]} rotation={[0, 0, i === 0 ? 0.5 : -0.5]} castShadow>
          <capsuleGeometry args={[0.08, 0.25, 6, 8]} />
          <meshStandardMaterial color={sackColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Stubby legs */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.15, 0]} castShadow>
          <capsuleGeometry args={[0.08, 0.15, 6, 8]} />
          <meshStandardMaterial color={sackColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Bugs visible through burlap */}
      {world === 'spectral' && [[0.15, 0.5, 0.35], [-0.2, 0.8, 0.3], [0.1, 0.4, 0.38]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial color="#2a2a2a" />
        </mesh>
      ))}
    </group>
  );
}

export function TrickOrTreaterModel({ world, hovered }: NPCModelProps) {
  const clothColor = world === 'spectral' ? '#8B0000' : '#aa2222';
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Devil/witch costume body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <coneGeometry args={[0.12, 0.4, 8]} />
        <meshStandardMaterial
          color={clothColor}
          roughness={0.8}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Mask face */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <sphereGeometry args={[0.1, 10, 10]} />
        <meshStandardMaterial color="#ff6600" roughness={0.7} />
      </mesh>
      {/* Devil horns or witch hat based on variation */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.65, 0]} rotation={[0, 0, i === 0 ? -0.3 : 0.3]} castShadow>
          <coneGeometry args={[0.02, 0.08, 6]} />
          <meshStandardMaterial color="#ff0000" />
        </mesh>
      ))}
      {/* Evil grin on mask */}
      <mesh position={[0, 0.5, 0.09]}>
        <torusGeometry args={[0.04, 0.008, 6, 12, Math.PI]} />
        <meshStandardMaterial color="#000000" />
      </mesh>
      {/* Eye holes */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.57, 0.08]}>
          <circleGeometry args={[0.015, 6]} />
          <meshStandardMaterial color="#000000" />
        </mesh>
      ))}
      {/* Arms */}
      {[-0.12, 0.12].map((x, i) => (
        <mesh key={i} position={[x, 0.35, 0]} rotation={[0, 0, i === 0 ? 0.5 : -0.5]} castShadow>
          <capsuleGeometry args={[0.025, 0.1, 4, 8]} />
          <meshStandardMaterial color={clothColor} roughness={0.8} />
        </mesh>
      ))}
      {/* Trick or treat bag */}
      <mesh position={[0.15, 0.25, 0.08]} castShadow>
        <sphereGeometry args={[0.06, 8, 8]} />
        <meshStandardMaterial color="#ff6600" roughness={0.9} />
      </mesh>
      {/* Legs */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.08, 0]} castShadow>
          <capsuleGeometry args={[0.025, 0.08, 4, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

export function TwoFaceMayorModel({ world, hovered }: NPCModelProps) {
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Round body - mayor outfit */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.18, 0.5, 12]} />
        <meshStandardMaterial
          color="#1a1a1a"
          roughness={0.7}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* White collar */}
      <mesh position={[0, 0.55, 0]}>
        <torusGeometry args={[0.12, 0.03, 8, 16]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      {/* Cone-shaped head (iconic two-face design) */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <coneGeometry args={[0.15, 0.35, 12]} />
        <meshStandardMaterial color={world === 'spectral' ? '#e8e0d0' : '#f0e8e0'} roughness={0.8} />
      </mesh>
      {/* Happy side features */}
      <mesh position={[-0.05, 0.75, 0.12]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
      <mesh position={[-0.06, 0.68, 0.1]}>
        <torusGeometry args={[0.025, 0.005, 6, 8, Math.PI]} />
        <meshStandardMaterial color="#ff6666" />
      </mesh>
      {/* Sad/worried side features */}
      <mesh position={[0.05, 0.75, 0.12]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshBasicMaterial color="#000000" />
      </mesh>
      <mesh position={[0.06, 0.7, 0.1]} rotation={[Math.PI, 0, 0]}>
        <torusGeometry args={[0.025, 0.005, 6, 8, Math.PI]} />
        <meshStandardMaterial color="#6666aa" />
      </mesh>
      {/* Top hat */}
      <mesh position={[0, 1, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.1, 0.15, 12]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
      </mesh>
      <mesh position={[0, 0.95, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.15, 0.02, 12]} />
        <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
      </mesh>
      {/* Spider badge on hat */}
      <mesh position={[0, 1.05, 0.11]}>
        <sphereGeometry args={[0.02, 6, 6]} />
        <meshStandardMaterial color="#ff6600" />
      </mesh>
      {/* Short arms */}
      {[-0.18, 0.18].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0]} rotation={[0, 0, i === 0 ? 0.3 : -0.3]} castShadow>
          <capsuleGeometry args={[0.03, 0.1, 4, 8]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
        </mesh>
      ))}
    </group>
  );
}

export function MadScientistModel({ world, hovered }: NPCModelProps) {
  const skinColor = world === 'spectral' ? '#a0a090' : '#c0c0b0';
  const highlightColor = hovered ? '#ff6600' : undefined;

  return (
    <group>
      {/* Wheelchair/chair base */}
      <mesh position={[0, 0.15, 0]} castShadow>
        <boxGeometry args={[0.3, 0.15, 0.25]} />
        <meshStandardMaterial color="#4a4a4a" roughness={0.8} />
      </mesh>
      {/* Wheels */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.1, -0.1]} rotation={[0, Math.PI / 2, 0]} castShadow>
          <torusGeometry args={[0.1, 0.02, 8, 16]} />
          <meshStandardMaterial color="#2a2a2a" roughness={0.7} />
        </mesh>
      ))}
      {/* Body in lab coat */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.3, 8]} />
        <meshStandardMaterial
          color="#e8e8e0"
          roughness={0.7}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Large exposed brain head */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.8} />
      </mesh>
      {/* Top of skull - removable (showing brain) */}
      <mesh position={[0, 0.72, 0]} castShadow>
        <sphereGeometry args={[0.12, 10, 10, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#ffaaaa" roughness={0.6} />
      </mesh>
      {/* Brain wrinkles */}
      {[[-0.03, 0.75], [0.04, 0.73], [0, 0.77]].map(([x, y], i) => (
        <mesh key={i} position={[x, y, 0]}>
          <torusGeometry args={[0.03, 0.008, 6, 8]} />
          <meshStandardMaterial color="#ff8888" roughness={0.7} />
        </mesh>
      ))}
      {/* Small angry eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.6, 0.12]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshBasicMaterial color="#000000" />
        </mesh>
      ))}
      {/* Glasses */}
      <mesh position={[0, 0.6, 0.13]}>
        <torusGeometry args={[0.08, 0.008, 6, 16]} />
        <meshStandardMaterial color="#2a2a2a" />
      </mesh>
      {/* Thin arms */}
      {[-0.15, 0.15].map((x, i) => (
        <mesh key={i} position={[x, 0.4, 0]} rotation={[0, 0, i === 0 ? 0.5 : -0.5]} castShadow>
          <capsuleGeometry args={[0.025, 0.12, 4, 8]} />
          <meshStandardMaterial color="#e8e8e0" roughness={0.7} />
        </mesh>
      ))}
      {/* Skeletal hands */}
      {[-0.22, 0.22].map((x, i) => (
        <mesh key={i} position={[x, 0.3, 0]} castShadow>
          <sphereGeometry args={[0.025, 6, 6]} />
          <meshStandardMaterial color={skinColor} roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}

// Registry of all Nightmare Before Christmas character models
export const nightmareChristmasNPCs: NPCModelRegistry = {
  // Christmas Town
  santa_claus: SantaClausModel,
  christmas_elf: ChristmasElfModel,
  reindeer: ReindeerModel,
  gingerbread_man: GingerbreadManModel,
  // Halloween Town
  jack_skellington: JackSkellingtonModel,
  sally: SallyModel,
  ghost_dog: GhostDogModel,
  oogie_boogie: OogieBoogieModel,
  trick_or_treater: TrickOrTreaterModel,
  two_face_mayor: TwoFaceMayorModel,
  mad_scientist: MadScientistModel,
};
